import {
  addDays,
  format,
  formatDuration,
  intervalToDuration,
  isBefore,
  isToday,
  startOfDay,
} from 'date-fns';
import { useEffect, useState } from 'react';

const UPCOMING_WEEK_DAYS = 7;

export type UseCountdownVariant = 'board' | 'carousel';

export type UseCountdownOptions = {
  variant?: UseCountdownVariant;
};

function formatWithVariant(date: Date, variant: UseCountdownVariant, now: Date): string {
  const startOfToday = startOfDay(now);
  const endOfUpcomingWeek = addDays(startOfToday, UPCOMING_WEEK_DAYS);
  if (isToday(date)) {
    const time = format(date, 'h:mm a');
    return variant === 'carousel' ? `Today at ${time}` : time;
  }
  const isUpcomingWeek = !isBefore(date, startOfToday) && isBefore(date, endOfUpcomingWeek);
  if (isUpcomingWeek) return format(date, 'EEE h:mm a');
  if (variant === 'board') return format(date, 'M/dd h:mm a');
  const duration = intervalToDuration({ start: now, end: date });
  return `In ${formatDuration({ days: duration.days ?? 0 })}`;
}

export const useCountdown = (targetDate: Date, options: UseCountdownOptions = {}) => {
  const { variant } = options;
  const [timeLeft, setTimeLeft] = useState('');
  const [isApproachingDeadline, setIsApproachingDeadline] = useState(false);
  const [hasDeadlinePassed, setHasDeadlinePassed] = useState(false);

  useEffect(() => {
    if (!targetDate) return undefined;

    const now = new Date();

    if (variant) {
      setTimeLeft(formatWithVariant(targetDate, variant, now));
      if (now >= targetDate) setHasDeadlinePassed(true);
      return undefined;
    }

    const updateCountdown = () => {
      const n = new Date();

      if (n >= targetDate) {
        setTimeLeft('00:00:00');
        setHasDeadlinePassed(true);
      } else {
        const duration = intervalToDuration({ start: n, end: targetDate });
        const totalHours = (duration.days || 0) * 24 + (duration.hours || 0);
        const totalMonths = (duration.years || 0) * 12 + (duration.months || 0);

        setIsApproachingDeadline((prev) => (totalHours < 2 ? true : prev));

        const defaultFormatted =
          duration.days > 0 || totalMonths > 0
            ? `${totalMonths ? `${totalMonths}m ` : ''}${duration.days}d ${duration.hours}h`
            : [
                String(duration.hours).padStart(2, '0'),
                String(duration.minutes || 0).padStart(2, '0'),
                String(duration.seconds || 0).padStart(2, '0'),
              ].join(':');

        setTimeLeft(defaultFormatted);
      }
    };

    updateCountdown();

    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, [targetDate, variant]);

  return { timeLeft, isApproachingDeadline, hasDeadlinePassed };
};
